'use client';

import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { domId, sanitizeReactIdSuffix } from '@/lib/dom-id';
import { DUPLICATE_ERROR, isDuplicateError } from '@/lib/duplicate-errors';
import { AuthConfig } from '@/services/auth-config-service';
import { getOauth2Info, OauthInfo } from '@/services/system-service';
import { replaceLocationHash } from '@/lib/route-fragment';
import { isActionPayloadTooLargeError } from '@/services/action-service';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';

interface ManageAuthComponentProps {
  systemId: string;
  existingConfig?: AuthConfig | null;
  draftConfig?: Partial<AuthConfig> | null;
  onDraftChange?: (config: Partial<AuthConfig> | null) => void;
  onSave: (config: AuthConfig) => Promise<void>;
  onCancel?: () => void;
  /**
   * Phase 5: auth mode for ref/template systems.
   * "shared"         → creds pre-configured by template owner; show read-only notice.
   * "own-input"      → workspace must supply own creds (share_auth=false).
   * "not-applicable" → own system, normal flow (default).
   */
  authMode?: 'shared' | 'own-input' | 'not-applicable';
  canManage?: boolean;
  canUseOAuth?: boolean;
}

type AuthType = 'none' | 'api_key' | 'basic_auth' | 'bearer_token' | 'oauth2';
type FormErrors = {
  save?: string;
};
type ValidationErrors = {
  apiKeyName?: string;
  apiKeyValue?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  bearerTokenValue?: string;
  oauth2ClientId?: string;
  oauth2ClientSecret?: string;
  oauth2TokenUrl?: string;
  oauth2AuthorizationUrl?: string;
  oauth2Issuer?: string;
};

function requestsOpenID(scopes?: string[]): boolean {
  return scopes?.some(scope => scope.split(/\s+/).includes('openid')) ?? false;
}

const NoneIcon = ({ isSelected = false }: { isSelected?: boolean }) => {
  const reactId = sanitizeReactIdSuffix(useId()) || 'id';
  const gradientId = domId('icn', `auth-none-gradient-${reactId}`);

  return (
    <svg
      className="w-5 h-5"
      viewBox="0 0 20 20"
      fill="none"
      style={{ shapeRendering: 'geometricPrecision' }}
    >
      {/* House shape */}
      <path
        d="M7.43066 0.868164C8.94824 -0.289218 11.0518 -0.289218 12.5693 0.868164L18.332 5.26367C19.3829 6.06516 20 7.31116 20 8.63281V15.7627C19.9999 18.1028 18.1028 20 15.7627 20H4.2373C1.89716 20 0.000101529 18.1028 0 15.7627V8.63281C0 7.31116 0.617079 6.06516 1.66797 5.26367L7.43066 0.868164Z"
        fill={isSelected ? `url(#${gradientId})` : 'none'}
        stroke={isSelected ? 'none' : '#6D7E91'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Lock icon */}
      <path
        d="M10 7C8.34315 7 7 8.34315 7 10V13C7.00012 14.6568 8.34322 16 10 16C11.6568 16 12.9999 14.6568 13 13V10C13 8.34315 11.6569 7 10 7Z"
        fill={isSelected ? 'white' : 'none'}
        stroke={isSelected ? 'none' : '#6D7E91'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="10"
          x2="20"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const APIIcon = ({ isSelected = false }: { isSelected?: boolean }) => {
  const reactId = sanitizeReactIdSuffix(useId()) || 'id';
  const gradientId = domId('icn', `auth-api-gradient-${reactId}`);

  return (
    <svg width="21" height="21" viewBox="0 0 21 21" fill="none">
      {isSelected ? (
        <>
          {/* Filled state - monitor background */}
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2.26074 0H18.0391C19.287 0.000230631 20.2988 1.01271 20.2988 2.26074V13.5303C20.2986 14.7781 19.2869 15.7898 18.0391 15.79H12.4102V18.9971H14.9795C15.3383 18.9972 15.6296 19.2887 15.6299 19.6475C15.6299 20.0064 15.3384 20.2978 14.9795 20.2979H5.31934C4.96035 20.2979 4.66895 20.0064 4.66895 19.6475C4.66919 19.2887 4.9605 18.9971 5.31934 18.9971H7.88867V15.79H2.26074C1.01273 15.79 0.000243877 14.7782 0 13.5303V2.26074C0 1.01257 1.01258 0 2.26074 0Z"
            fill={`url(#${gradientId})`}
          />
          <path d="M9.18945 15.79V18.9971H11.1094V15.79H9.18945Z" fill={`url(#${gradientId})`} />
          {/* White letters A, P, I */}
          <path
            d="M5.31934 4.02441C6.56732 4.02456 7.57903 5.03714 7.5791 6.28516V11.1152C7.57886 11.474 7.28755 11.7646 6.92871 11.7646C6.57012 11.7644 6.27954 11.4738 6.2793 11.1152V8.54492H4.35938V11.1152C4.35914 11.474 4.06782 11.7646 3.70898 11.7646C3.3502 11.7646 3.05883 11.474 3.05859 11.1152V6.28516C3.05867 5.03708 4.07128 4.02448 5.31934 4.02441ZM5.31934 5.3252C4.78919 5.32526 4.35945 5.75511 4.35938 6.28516V7.24414H6.2793V6.28516C6.27922 5.75517 5.84941 5.32534 5.31934 5.3252Z"
            fill="white"
          />
          <path
            d="M11.7598 4.02441C13.0078 4.02456 14.0195 5.03714 14.0195 6.28516C14.0193 7.53308 13.0077 8.54477 11.7598 8.54492H10.7998V11.1152C10.7996 11.474 10.5083 11.7646 10.1494 11.7646C9.79058 11.7646 9.49926 11.474 9.49902 11.1152V4.6748C9.4991 4.31588 9.79047 4.02441 10.1494 4.02441H11.7598ZM10.7998 7.24512H11.7598C12.2898 7.24497 12.7195 6.81505 12.7197 6.28516C12.7197 5.75517 12.2898 5.32534 11.7598 5.3252H10.7998V7.24512Z"
            fill="white"
          />
          <path
            d="M16.5898 4.02441C16.9487 4.02448 17.2402 4.31592 17.2402 4.6748V11.1152C17.24 11.474 16.9486 11.7646 16.5898 11.7646C16.231 11.7646 15.9397 11.474 15.9395 11.1152V4.6748C15.9395 4.31588 16.2309 4.02441 16.5898 4.02441Z"
            fill="white"
          />
        </>
      ) : (
        /* Outline state - stroke only */
        <path
          d="M18.0391 0C19.287 0.000230631 20.2988 1.01271 20.2988 2.26074V13.5303C20.2986 14.7781 19.2869 15.7898 18.0391 15.79H12.4102V18.9971H14.9795C15.3383 18.9972 15.6296 19.2887 15.6299 19.6475C15.6299 20.0064 15.3384 20.2978 14.9795 20.2979H5.31934C4.96035 20.2979 4.66895 20.0064 4.66895 19.6475C4.66919 19.2887 4.9605 18.9971 5.31934 18.9971H7.88867V15.79H2.26074C1.01273 15.79 0.000243877 14.7782 0 13.5303V2.26074C0 1.01257 1.01258 0 2.26074 0H18.0391ZM9.18945 15.79V18.9971H11.1094V15.79H9.18945ZM2.26074 1.30078C1.73049 1.30078 1.30078 1.7306 1.30078 2.26074V13.5303C1.30102 14.0602 1.73064 14.4902 2.26074 14.4902H8.5293C8.53255 14.4902 8.5358 14.4893 8.53906 14.4893C8.54224 14.4893 8.54566 14.4902 8.54883 14.4902H11.75C11.7532 14.4902 11.7566 14.4893 11.7598 14.4893C11.763 14.4893 11.7663 14.4902 11.7695 14.4902H18.0391C18.569 14.49 18.9988 14.0601 18.999 13.5303V2.26074C18.999 1.73074 18.5691 1.30101 18.0391 1.30078H2.26074ZM5.31934 4.02441C6.56732 4.02456 7.57903 5.03714 7.5791 6.28516V11.1152C7.57886 11.474 7.28755 11.7646 6.92871 11.7646C6.57012 11.7644 6.27954 11.4738 6.2793 11.1152V8.54492H4.35938V11.1152C4.35914 11.474 4.06782 11.7646 3.70898 11.7646C3.3502 11.7646 3.05883 11.474 3.05859 11.1152V6.28516C3.05867 5.03708 4.07128 4.02448 5.31934 4.02441ZM11.7598 4.02441C13.0078 4.02456 14.0195 5.03714 14.0195 6.28516C14.0193 7.53308 13.0077 8.54477 11.7598 8.54492H10.7998V11.1152C10.7996 11.474 10.5083 11.7646 10.1494 11.7646C9.79058 11.7646 9.49926 11.474 9.49902 11.1152V4.6748C9.4991 4.31588 9.79047 4.02441 10.1494 4.02441H11.7598ZM16.5898 4.02441C16.9487 4.02448 17.2402 4.31592 17.2402 4.6748V11.1152C17.24 11.474 16.9486 11.7646 16.5898 11.7646C16.231 11.7646 15.9397 11.474 15.9395 11.1152V4.6748C15.9395 4.31588 16.2309 4.02441 16.5898 4.02441ZM10.7998 7.24512H11.7598C12.2898 7.24497 12.7195 6.81505 12.7197 6.28516C12.7197 5.75517 12.2898 5.32534 11.7598 5.3252H10.7998V7.24512ZM5.31934 5.3252C4.78919 5.32526 4.35945 5.75511 4.35938 6.28516V7.24414H6.2793V6.28516C6.27922 5.75517 5.84941 5.32534 5.31934 5.3252Z"
          fill="#6D7E91"
        />
      )}
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="10"
          x2="21"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const BasicAuthIcon = ({ isSelected = false }: { isSelected?: boolean }) => {
  const reactId = sanitizeReactIdSuffix(useId()) || 'id';
  const gradientId = domId('icn', `auth-basic-gradient-${reactId}`);

  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      {isSelected ? (
        <>
          {/* Filled state - gear background */}
          <path
            d="M12.9746 2.53027L15.3242 3.81836L18.2363 3.5498L20 6.4502L18.2979 8.71191V11.2881L20 13.5498L18.2363 16.4502L15.3242 16.1816L12.9746 17.4697L11.7637 20H8.23633L7.02539 17.4697L4.67676 16.1816L1.76367 16.4502L0 13.5498L1.70215 11.2881V8.71191L0 6.4502L1.76367 3.5498L4.67676 3.81836L7.02539 2.53027L8.23633 0H11.7637L12.9746 2.53027Z"
            fill={`url(#${gradientId})`}
          />
          {/* White person icon inside */}
          <circle cx="10" cy="8" r="2.5" fill="white" />
          <path d="M10 11.5C7.5 11.5 5.5 13 5.5 15H14.5C14.5 13 12.5 11.5 10 11.5Z" fill="white" />
        </>
      ) : (
        <>
          {/* Outline state - gear outline */}
          <path
            d="M12.9746 2.53027L15.3242 3.81836L18.2363 3.5498L20 6.4502L18.2979 8.71191V11.2881L20 13.5498L18.2363 16.4502L15.3242 16.1816L12.9746 17.4697L11.7637 20H8.23633L7.02539 17.4697L4.67676 16.1816L1.76367 16.4502L0 13.5498L1.70215 11.2881V8.71191L0 6.4502L1.76367 3.5498L4.67676 3.81836L7.02539 2.53027L8.23633 0H11.7637L12.9746 2.53027Z"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
          {/* Person icon outline */}
          <circle cx="10" cy="8" r="2.5" fill="none" stroke="#6D7E91" strokeWidth="1.2" />
          <path
            d="M10 11.5C7.5 11.5 5.5 13 5.5 15H14.5C14.5 13 12.5 11.5 10 11.5Z"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
        </>
      )}
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="10"
          x2="20"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const BearerTokenIcon = ({ isSelected = false }: { isSelected?: boolean }) => {
  const reactId = sanitizeReactIdSuffix(useId()) || 'id';
  const gradientId = domId('icn', `auth-bearer-gradient-${reactId}`);

  return (
    <svg width="20" height="21" viewBox="0 0 20 21" fill="none">
      {isSelected ? (
        <>
          {/* Filled state - shield background */}
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M1.49805 4.61819L1.49805 9.16089C1.49805 14.2611 4.38403 17.2399 6.91524 18.6477L7.2112 18.8061L7.50271 18.9502C7.55086 18.973 7.59878 18.9953 7.64643 19.0169L7.92908 19.14L8.20434 19.2492L8.47106 19.3449C8.55841 19.3745 8.64414 19.4019 8.72805 19.4271L8.97415 19.4962L9.20818 19.5522C9.43578 19.6018 9.64352 19.6323 9.82612 19.6444L10.0002 19.6504L10.1742 19.6444C10.3568 19.6323 10.5645 19.6018 10.7921 19.5522L11.0262 19.4962L11.2723 19.4271C11.3562 19.4019 11.4419 19.3745 11.5292 19.3449L11.796 19.2492L12.0712 19.14L12.3539 19.0169C12.4015 18.9953 12.4494 18.973 12.4976 18.9502L12.7891 18.8061L13.0851 18.6477C15.6163 17.2399 18.5023 14.2611 18.5023 9.16089V4.61819C18.502 3.82761 17.9211 3.17321 17.175 3.12314C15.8283 3.03173 14.6176 2.79654 13.5427 2.41764C12.6368 2.0983 11.8274 1.67688 11.1145 1.15338L10.8522 0.951554C10.3476 0.550003 9.65268 0.550003 9.1481 0.951554C8.37378 1.56993 7.47678 2.05839 6.45761 2.41764C5.49022 2.75865 4.41277 2.98325 3.22524 3.09146L2.82532 3.12314C2.07923 3.17321 1.49827 3.82761 1.49805 4.61819Z"
            fill={`url(#${gradientId})`}
          />
          {/* White checkmark */}
          <path
            d="M13.118 8.16602L9.13483 12.7183L7.42773 10.4421"
            stroke="white"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      ) : (
        <>
          {/* Outline state - shield stroke only */}
          <path
            d="M1.49805 4.61819L1.49805 9.16089C1.49805 14.2611 4.38403 17.2399 6.91524 18.6477L7.2112 18.8061L7.50271 18.9502C7.55086 18.973 7.59878 18.9953 7.64643 19.0169L7.92908 19.14L8.20434 19.2492L8.47106 19.3449C8.55841 19.3745 8.64414 19.4019 8.72805 19.4271L8.97415 19.4962L9.20818 19.5522C9.43578 19.6018 9.64352 19.6323 9.82612 19.6444L10.0002 19.6504L10.1742 19.6444C10.3568 19.6323 10.5645 19.6018 10.7921 19.5522L11.0262 19.4962L11.2723 19.4271C11.3562 19.4019 11.4419 19.3745 11.5292 19.3449L11.796 19.2492L12.0712 19.14L12.3539 19.0169C12.4015 18.9953 12.4494 18.973 12.4976 18.9502L12.7891 18.8061L13.0851 18.6477C15.6163 17.2399 18.5023 14.2611 18.5023 9.16089V4.61819C18.502 3.82761 17.9211 3.17321 17.175 3.12314C15.8283 3.03173 14.6176 2.79654 13.5427 2.41764C12.6368 2.0983 11.8274 1.67688 11.1145 1.15338L10.8522 0.951554C10.3476 0.550003 9.65268 0.550003 9.1481 0.951554C8.37378 1.56993 7.47678 2.05839 6.45761 2.41764C5.49022 2.75865 4.41277 2.98325 3.22524 3.09146L2.82532 3.12314C2.07923 3.17321 1.49827 3.82761 1.49805 4.61819Z"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
          {/* Checkmark stroke */}
          <path
            d="M13.118 8.16602L9.13483 12.7183L7.42773 10.4421"
            stroke="#6D7E91"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </>
      )}
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="10"
          x2="20"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const Oauth2Icon = ({ isSelected = false }: { isSelected?: boolean }) => {
  const reactId = sanitizeReactIdSuffix(useId()) || 'id';
  const gradientId = domId('icn', `auth-oauth-gradient-${reactId}`);

  return (
    <svg width="20" height="21" viewBox="0 0 20 21" fill="none">
      {isSelected ? (
        <>
          {/* Filled state - shield with document and database */}
          <path
            d="M9.46 0.75C9.78 0.55 10.22 0.55 10.54 0.75L10.85 0.95C11.5 1.4 12.25 1.77 13.1 2.05C14.1 2.4 15.25 2.6 16.5 2.69L16.7 2.71C17.5 2.85 18.1 3.55 18.1 4.37V9.16C18.1 13.5 15.6 16.2 13.3 17.5L10 19.1L6.7 17.5C4.4 16.2 1.9 13.5 1.9 9.16V4.37C1.9 3.55 2.5 2.85 3.3 2.71L3.5 2.69C4.75 2.6 5.9 2.4 6.9 2.05C7.75 1.77 8.5 1.4 9.15 0.95L9.46 0.75Z"
            fill={`url(#${gradientId})`}
          />
          {/* White lines */}
          <rect x="4" y="5.5" width="6" height="1" rx="0.5" fill="white" />
          <rect x="4" y="7.5" width="5" height="1" rx="0.5" fill="white" />
          <rect x="4" y="9.5" width="4" height="1" rx="0.5" fill="white" />
          {/* Database cylinder */}
          <ellipse
            cx="15"
            cy="12"
            rx="3.5"
            ry="1.5"
            fill={`url(#${gradientId})`}
            stroke="white"
            strokeWidth="0.5"
          />
          <path
            d="M11.5 12V17C11.5 17.83 13.07 18.5 15 18.5C16.93 18.5 18.5 17.83 18.5 17V12"
            fill={`url(#${gradientId})`}
            stroke="white"
            strokeWidth="0.5"
          />
          <ellipse
            cx="15"
            cy="14.5"
            rx="3.5"
            ry="1.2"
            fill="none"
            stroke="white"
            strokeWidth="0.5"
            strokeOpacity="0.5"
          />
        </>
      ) : (
        <>
          {/* Outline state - shield stroke */}
          <path
            d="M9.46 0.75C9.78 0.55 10.22 0.55 10.54 0.75L10.85 0.95C11.5 1.4 12.25 1.77 13.1 2.05C14.1 2.4 15.25 2.6 16.5 2.69L16.7 2.71C17.5 2.85 18.1 3.55 18.1 4.37V9.16C18.1 13.5 15.6 16.2 13.3 17.5L10 19.1L6.7 17.5C4.4 16.2 1.9 13.5 1.9 9.16V4.37C1.9 3.55 2.5 2.85 3.3 2.71L3.5 2.69C4.75 2.6 5.9 2.4 6.9 2.05C7.75 1.77 8.5 1.4 9.15 0.95L9.46 0.75Z"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
          {/* Lines stroke */}
          <rect x="4" y="5.5" width="6" height="1" rx="0.5" fill="#6D7E91" />
          <rect x="4" y="7.5" width="5" height="1" rx="0.5" fill="#6D7E91" />
          <rect x="4" y="9.5" width="4" height="1" rx="0.5" fill="#6D7E91" />
          {/* Database cylinder stroke */}
          <ellipse
            cx="15"
            cy="12"
            rx="3.5"
            ry="1.5"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
          <path
            d="M11.5 12V17C11.5 17.83 13.07 18.5 15 18.5C16.93 18.5 18.5 17.83 18.5 17V12"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="1.2"
          />
          <ellipse
            cx="15"
            cy="14.5"
            rx="3.5"
            ry="1.2"
            fill="none"
            stroke="#6D7E91"
            strokeWidth="0.8"
            strokeOpacity="0.6"
          />
        </>
      )}
      <defs>
        <linearGradient
          id={gradientId}
          x1="0"
          y1="10"
          x2="20"
          y2="10"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const AUTH_TYPES: {
  value: AuthType;
  labelKey: string;
  descriptionKey: string;
  icon: (isSelected: boolean) => React.ReactNode;
}[] = [
  {
    value: 'none',
    labelKey: 'authTypes.none.label',
    descriptionKey: 'authTypes.none.description',
    icon: isSelected => <NoneIcon isSelected={isSelected} />,
  },
  {
    value: 'api_key',
    labelKey: 'authTypes.apiKey.label',
    descriptionKey: 'authTypes.apiKey.description',
    icon: isSelected => <APIIcon isSelected={isSelected} />,
  },
  {
    value: 'basic_auth',
    labelKey: 'authTypes.basicAuth.label',
    descriptionKey: 'authTypes.basicAuth.description',
    icon: isSelected => <BasicAuthIcon isSelected={isSelected} />,
  },
  {
    value: 'bearer_token',
    labelKey: 'authTypes.bearerToken.label',
    descriptionKey: 'authTypes.bearerToken.description',
    icon: isSelected => <BearerTokenIcon isSelected={isSelected} />,
  },
  {
    value: 'oauth2',
    labelKey: 'authTypes.oauth2.label',
    descriptionKey: 'authTypes.oauth2.description',
    icon: isSelected => <Oauth2Icon isSelected={isSelected} />,
  },
];

type ManageMode = 'view' | 'edit';

function cloneAuthConfig(config?: Partial<AuthConfig> | null): Partial<AuthConfig> {
  if (!config) return {};
  try {
    return structuredClone(config);
  } catch {
    return JSON.parse(JSON.stringify(config)) as AuthConfig;
  }
}

function persistableFields(config?: AuthConfig | null): Partial<AuthConfig> {
  if (!config) return {};
  const { authconfig_id, workspace_id, organization_id } = config;
  return {
    ...(authconfig_id ? { authconfig_id } : {}),
    ...(workspace_id ? { workspace_id } : {}),
    ...(organization_id ? { organization_id } : {}),
  };
}

function getDefaultConfigForType(type: AuthType): Partial<AuthConfig> {
  switch (type) {
    case 'api_key':
      return {
        api_key: {
          name: '',
          value: '',
          location: 'header',
        },
      };
    case 'basic_auth':
      return {
        basic_auth: {
          username: '',
          password: '',
        },
      };
    case 'bearer_token':
      return {
        bearer_token: {
          token: '',
          header_name: 'Authorization',
        },
      };
    case 'oauth2':
      return {
        oauth2: {
          client_id: '',
          client_secret: '',
          token_url: '',
          authorization_url: '',
          issuer: '',
          scopes: [],
          auth_params: {},
        },
      };
    case 'none':
    default:
      return {};
  }
}

function buildFormState(type: AuthType, source?: Partial<AuthConfig>): Partial<AuthConfig> {
  const defaults = getDefaultConfigForType(type);

  const merged: Partial<AuthConfig> = {
    ...defaults,
    ...source,
  };

  if (defaults.api_key || source?.api_key) {
    merged.api_key = {
      name: source?.api_key?.name ?? defaults.api_key?.name ?? '',
      value: source?.api_key?.value ?? defaults.api_key?.value ?? '',
      location: source?.api_key?.location ?? defaults.api_key?.location ?? 'header',
    };
  }

  if (defaults.basic_auth || source?.basic_auth) {
    merged.basic_auth = {
      username: source?.basic_auth?.username ?? defaults.basic_auth?.username ?? '',
      password: source?.basic_auth?.password ?? defaults.basic_auth?.password ?? '',
    };
  }

  if (defaults.bearer_token || source?.bearer_token) {
    merged.bearer_token = {
      token: source?.bearer_token?.token ?? defaults.bearer_token?.token ?? '',
      header_name:
        source?.bearer_token?.header_name ?? defaults.bearer_token?.header_name ?? 'Authorization',
    };
  }

  if (defaults.oauth2 || source?.oauth2) {
    // Convert scopes from string to array if needed (backend returns string)
    // Use unknown cast because backend may return string but interface expects string[]
    // const sourceScopes = source?.oauth2?.scopes as unknown;
    // const defaultScopes = defaults.oauth2?.scopes as unknown;

    // let scopes: string[] = [];
    // if (typeof sourceScopes === 'string') {
    //   scopes = sourceScopes ? (sourceScopes as string).split(',').map((s: string) => s.trim()).filter(Boolean) : [];
    // } else if (Array.isArray(sourceScopes)) {
    //   scopes = sourceScopes as string[];
    // } else if (typeof defaultScopes === 'string') {
    //   scopes = defaultScopes ? (defaultScopes as string).split(',').map((s: string) => s.trim()).filter(Boolean) : [];
    // } else if (Array.isArray(defaultScopes)) {
    //   scopes = defaultScopes as string[];
    // }

    merged.oauth2 = {
      client_id: source?.oauth2?.client_id ?? defaults.oauth2?.client_id ?? '',
      client_secret: source?.oauth2?.client_secret ?? defaults.oauth2?.client_secret ?? '',
      token_url: source?.oauth2?.token_url ?? defaults.oauth2?.token_url ?? '',
      authorization_url:
        source?.oauth2?.authorization_url ?? defaults.oauth2?.authorization_url ?? '',
      issuer: source?.oauth2?.issuer ?? defaults.oauth2?.issuer ?? '',
      scopes: source?.oauth2?.scopes ?? defaults.oauth2?.scopes ?? [],
      auth_params: source?.oauth2?.auth_params ?? defaults.oauth2?.auth_params ?? {},
    };
  }

  return merged;
}

export default function ManageAuthComponent({
  systemId,
  existingConfig,
  draftConfig,
  onDraftChange,
  onSave,
  onCancel,
  authMode = 'not-applicable',
  canManage = true,
  canUseOAuth = true,
}: ManageAuthComponentProps) {
  const t = useTranslations('ManageAuth');
  const locale = useLocale();
  const basePurpose = `manage-auth-${systemId}`;
  const [selectedType, setSelectedType] = useState<AuthType>('none');
  const [mode, setMode] = useState<ManageMode>('view');
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<AuthConfig>>({});
  const [oauth2Info, setOauth2Info] = useState<OauthInfo | null>(null);
  const [isLoadingOauth2Info, setIsLoadingOauth2Info] = useState(false);
  const [hasCopiedCallbackUrl, setHasCopiedCallbackUrl] = useState(false);
  const copyIndicatorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // State for show/hide sensitive fields
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showBearerToken, setShowBearerToken] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [hasInitializedDraftState, setHasInitializedDraftState] = useState(false);

  // Track original configuration to restore when switching back to original type
  const [originalConfig, setOriginalConfig] = useState<Partial<AuthConfig> | null>(null);
  const [originalType, setOriginalType] = useState<AuthType | null>(null);

  // Initialize form data from draft or existing config.
  // Once initialized, skip draftConfig changes — they originate from this
  // component's own onDraftChange calls and re-consuming them would loop.
  useEffect(() => {
    if (hasInitializedDraftState) {
      return;
    }

    if (draftConfig) {
      const restoredType = (draftConfig.auth_type ??
        existingConfig?.auth_type ??
        'none') as AuthType;

      setSelectedType(restoredType);
      setFormData(buildFormState(restoredType, cloneAuthConfig(draftConfig)));
      setMode(canManage && (restoredType !== 'oauth2' || canUseOAuth) ? 'edit' : 'view');
      setOriginalType(existingConfig?.auth_type ?? null);
      setOriginalConfig(existingConfig ? cloneAuthConfig(existingConfig) : null);
      setFormErrors({});
      setHasAttemptedSubmit(false);
      setHasInitializedDraftState(true);
      return;
    }

    if (existingConfig) {
      setSelectedType(existingConfig.auth_type);
      setFormData(buildFormState(existingConfig.auth_type, cloneAuthConfig(existingConfig)));
      setMode('view');
      // Store original config for restoration when switching types
      setOriginalType(existingConfig.auth_type);
      setOriginalConfig(cloneAuthConfig(existingConfig));
      setFormErrors({});
      setHasAttemptedSubmit(false);
      setHasInitializedDraftState(true);
    } else {
      setSelectedType('none');
      setFormData(buildFormState('none'));
      setMode(canManage ? 'edit' : 'view');
      setOriginalType(null);
      setOriginalConfig(null);
      setFormErrors({});
      setHasAttemptedSubmit(false);
      setHasInitializedDraftState(true);
    }
  }, [canManage, canUseOAuth, draftConfig, existingConfig, hasInitializedDraftState]);

  // Fetch OAuth2 info when OAuth2 is selected
  useEffect(() => {
    if (selectedType !== 'oauth2' || !canUseOAuth) {
      setOauth2Info(null);
      return;
    }

    const fetchOauth2Info = async () => {
      setIsLoadingOauth2Info(true);
      try {
        const info = await getOauth2Info(systemId);
        setOauth2Info(info);
      } catch (error) {
        console.error('Failed to fetch OAuth2 info:', error);
        setOauth2Info(null);
      } finally {
        setIsLoadingOauth2Info(false);
      }
    };

    fetchOauth2Info();
  }, [canUseOAuth, selectedType, systemId]);

  useEffect(() => {
    replaceLocationHash(selectedType === 'oauth2' ? '#oauth2' : '');
  }, [selectedType]);

  useEffect(() => {
    if (!onDraftChange) {
      return;
    }

    if (!hasInitializedDraftState) {
      return;
    }

    if (mode !== 'edit') {
      return;
    }

    onDraftChange({
      ...formData,
      system_id: systemId,
      auth_type: selectedType,
    });
  }, [formData, hasInitializedDraftState, mode, onDraftChange, selectedType, systemId]);

  const resetToSummary = useCallback(() => {
    if (existingConfig) {
      setMode('view');
      setSelectedType(existingConfig.auth_type);
      setFormData(buildFormState(existingConfig.auth_type, cloneAuthConfig(existingConfig)));
    } else {
      setMode('edit');
      setSelectedType('none');
      setFormData(buildFormState('none'));
    }
    setFormErrors({});
    setHasAttemptedSubmit(false);
  }, [existingConfig]);

  const startEditCurrent = useCallback(() => {
    if (!canManage) return;
    if (existingConfig?.auth_type === 'oauth2' && !canUseOAuth) return;
    if (existingConfig) {
      setMode('edit');
      setSelectedType(existingConfig.auth_type);
      setFormData(buildFormState(existingConfig.auth_type, cloneAuthConfig(existingConfig)));
    } else {
      setMode('edit');
    }
    setFormErrors({});
    setHasAttemptedSubmit(false);
  }, [canManage, canUseOAuth, existingConfig]);

  const handleSelectType = useCallback(
    (typeValue: AuthType) => {
      if (typeValue === 'oauth2' && !canUseOAuth) {
        return;
      }
      if (selectedType === typeValue) {
        return;
      }

      setSelectedType(typeValue);
      setFormErrors({});
      setHasAttemptedSubmit(false);

      // If no existing config, just build default form state
      if (!existingConfig) {
        setFormData(buildFormState(typeValue));
        return;
      }

      // In edit mode, allow switching between auth types
      if (mode === 'edit') {
        // If switching back to the original type, restore the original config
        if (typeValue === originalType && originalConfig) {
          setFormData(buildFormState(typeValue, originalConfig));
        } else {
          // Switching to a different type - use default values for new type
          // but preserve persistable fields (IDs)
          setFormData(
            buildFormState(typeValue, {
              ...persistableFields(existingConfig),
            })
          );
        }
        return;
      }

      // In view mode, just display the existing config
      setFormData(buildFormState(typeValue, cloneAuthConfig(existingConfig)));
    },
    [canUseOAuth, existingConfig, mode, selectedType, originalType, originalConfig]
  );

  const handleCopyCallbackUrl = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
    setHasCopiedCallbackUrl(true);
    if (copyIndicatorTimeout.current) {
      clearTimeout(copyIndicatorTimeout.current);
    }
    copyIndicatorTimeout.current = setTimeout(() => {
      setHasCopiedCallbackUrl(false);
    }, 1000);
  }, []);

  const validationErrors: ValidationErrors = {};
  if (selectedType === 'api_key') {
    if (!formData.api_key?.name?.trim()) {
      validationErrors.apiKeyName = t('errors.apiKeyHeaderRequired');
    }
    if (!formData.api_key?.value?.trim()) {
      validationErrors.apiKeyValue = t('errors.apiKeyValueRequired');
    }
  }
  if (selectedType === 'basic_auth') {
    if (!formData.basic_auth?.username?.trim()) {
      validationErrors.basicAuthUsername = t('errors.basicAuthUsernameRequired');
    }
    if (!formData.basic_auth?.password?.trim()) {
      validationErrors.basicAuthPassword = t('errors.basicAuthPasswordRequired');
    }
  }
  if (selectedType === 'bearer_token') {
    if (!formData.bearer_token?.token?.trim()) {
      validationErrors.bearerTokenValue = t('errors.bearerTokenRequired');
    }
  }
  if (selectedType === 'oauth2') {
    if (!formData.oauth2?.client_id?.trim()) {
      validationErrors.oauth2ClientId = t('errors.oauth2ClientIdRequired');
    }
    if (!formData.oauth2?.client_secret?.trim()) {
      validationErrors.oauth2ClientSecret = t('errors.oauth2ClientSecretRequired');
    }
    const tokenUrl = formData.oauth2?.token_url?.trim();
    if (!tokenUrl) {
      validationErrors.oauth2TokenUrl = t('errors.oauth2TokenUrlRequired');
    } else if (!/^https?:\/\/.+/.test(tokenUrl)) {
      validationErrors.oauth2TokenUrl = t('errors.oauth2TokenUrlInvalid');
    }
    const authUrl = formData.oauth2?.authorization_url?.trim();
    if (authUrl && !/^https?:\/\/.+/.test(authUrl)) {
      validationErrors.oauth2AuthorizationUrl = t('errors.oauth2AuthorizationUrlInvalid');
    }
    const issuer = formData.oauth2?.issuer?.trim();
    if (requestsOpenID(formData.oauth2?.scopes) && !issuer) {
      validationErrors.oauth2Issuer = t('errors.oauth2IssuerRequired');
    } else if (issuer && !/^https?:\/\/[^/?#]+(?:\/[^?#]*)?$/.test(issuer)) {
      validationErrors.oauth2Issuer = t('errors.oauth2IssuerInvalid');
    }
  }
  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const visibleValidationErrors = hasAttemptedSubmit ? validationErrors : {};

  const handleSave = async () => {
    setHasAttemptedSubmit(true);
    if (!canManage || (selectedType === 'oauth2' && !canUseOAuth) || hasValidationErrors) {
      return;
    }

    setIsSaving(true);
    setFormErrors({});
    try {
      // Build config, stripping empty optional fields before sending to backend
      const cleaned = { ...formData };
      if (cleaned.oauth2) {
        const { authorization_url, issuer, ...rest } = cleaned.oauth2;
        cleaned.oauth2 = authorization_url?.trim()
          ? { ...rest, authorization_url: authorization_url.trim() }
          : rest;
        if (issuer?.trim()) cleaned.oauth2.issuer = issuer.trim();
      }
      const configToSave: AuthConfig = {
        ...cleaned,
        system_id: systemId,
        auth_type: selectedType,
      };
      await onSave(configToSave);
      setMode('view');
      setHasAttemptedSubmit(false);
    } catch (error) {
      // In the add-connector flow this save also persists the step-1 name.
      setFormErrors({
        save: isDuplicateError(error, DUPLICATE_ERROR.connectorName)
          ? t('errors.connectorNameExists')
          : isActionPayloadTooLargeError(error)
            ? t('errors.actionPayloadTooLarge')
            : t('errors.saveFailed'),
      });
      console.error('Credential save failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Just call onCancel - don't save anything
    // Saving should only happen when user clicks "Save & Finish Setup"
    onCancel?.();
  };

  const emptyValue = t('defaults.empty');

  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return emptyValue;

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return emptyValue;

      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      };

      return date.toLocaleString(locale, options);
    } catch {
      return emptyValue;
    }
  };

  const renderConfigDisplay = (config: AuthConfig) => {
    switch (config.auth_type) {
      case 'api_key': {
        const locationLabel = (config.api_key?.location ?? 'header').toString().toUpperCase();
        return (
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">{t('labels.headerName')}:</span>{' '}
              {config.api_key?.name || t('placeholders.apiKeyName')}
            </div>
            <div>
              <span className="font-medium">{t('labels.location')}:</span> {locationLabel}
            </div>
            <div>
              <span className="font-medium">{t('labels.key')}:</span>{' '}
              <SensitiveValueDisplay
                purpose={`${basePurpose}-view-api-key-value`}
                value={config.api_key?.value}
                emptyValue={emptyValue}
              />
            </div>
          </div>
        );
      }
      case 'basic_auth':
        return (
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">{t('labels.username')}:</span>{' '}
              {config.basic_auth?.username || emptyValue}
            </div>
            <div>
              <span className="font-medium">{t('labels.password')}:</span>{' '}
              <SensitiveValueDisplay
                purpose={`${basePurpose}-view-basic-auth-password`}
                value={config.basic_auth?.password}
                emptyValue={emptyValue}
              />
            </div>
          </div>
        );
      case 'bearer_token':
        return (
          <div className="space-y-2 text-sm">
            <div>
              <span className="font-medium">{t('labels.headerName')}:</span>{' '}
              {config.bearer_token?.header_name || t('placeholders.bearerHeader')}
            </div>
            <div>
              <span className="font-medium">{t('labels.token')}:</span>{' '}
              <SensitiveValueDisplay
                purpose={`${basePurpose}-view-bearer-token`}
                value={config.bearer_token?.token}
                emptyValue={emptyValue}
              />
            </div>
          </div>
        );
      case 'oauth2':
        return (
          <div className="space-y-2 text-sm">
            {/* Callback URL Info in View Mode */}
            {oauth2Info?.callback_url && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5 mb-3">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-900 mb-1">
                      {t('labels.callbackUrl')}
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-white px-2 py-1 rounded border border-blue-200 text-blue-800 font-mono break-all">
                        {oauth2Info.callback_url}
                      </code>
                      <Button
                        data-testid={domId('btn', `${basePurpose}-view-copy-callback-url`)}
                        type="button"
                        onClick={() => handleCopyCallbackUrl(oauth2Info.callback_url)}
                        variant="unstyled"
                        className={`cursor-pointer flex-shrink-0 px-1.5 py-1 text-xs rounded border transition-all duration-200 ${
                          hasCopiedCallbackUrl
                            ? 'bg-blue-600 text-white border-blue-600 scale-105 shadow-sm'
                            : 'text-blue-700 border-transparent'
                        }`}
                        title={t('actions.copyToClipboard')}
                      >
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <span className="font-medium">{t('labels.clientId')}:</span>{' '}
              {config.oauth2?.client_id || emptyValue}
            </div>
            <div>
              <span className="font-medium">{t('labels.clientSecret')}:</span>{' '}
              <SensitiveValueDisplay
                purpose={`${basePurpose}-view-oauth2-client-secret`}
                value={config.oauth2?.client_secret}
                emptyValue={emptyValue}
              />
            </div>
            <div>
              <span className="font-medium">{t('labels.tokenUrl')}:</span>{' '}
              {config.oauth2?.token_url || emptyValue}
            </div>
            {config.oauth2?.authorization_url && (
              <div>
                <span className="font-medium">{t('labels.authorizationUrl')}:</span>{' '}
                {config.oauth2.authorization_url}
              </div>
            )}
            <div>
              <span className="font-medium">{t('labels.issuer')}:</span>{' '}
              {config.oauth2?.issuer || emptyValue}
            </div>
            {config.oauth2?.scopes && config.oauth2.scopes.length > 0 && (
              <div>
                <span className="font-medium">{t('labels.scopes')}:</span>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {config.oauth2.scopes.map((scope, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                    >
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {config.oauth2?.auth_params && Object.keys(config.oauth2.auth_params).length > 0 && (
              <div>
                <span className="font-medium">{t('labels.authParams')}:</span>
                <div className="mt-1.5 space-y-1">
                  {Object.entries(config.oauth2.auth_params).map(([key, value], index) => (
                    <div key={index} className="text-sm">
                      <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                        {key}
                      </span>
                      {': '}
                      <SensitiveValueDisplay
                        purpose={`${basePurpose}-view-oauth2-auth-param-${index}`}
                        value={String(value)}
                        emptyValue={emptyValue}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case 'none':
      default:
        return <div className="text-sm text-slate-600">{t('states.noAuthConfigured')}</div>;
    }
  };

  const renderContent = () => {
    if (mode === 'view' && existingConfig) {
      return (
        <div className="space-y-4">
          {renderConfigDisplay(existingConfig)}
          {existingConfig.last_updated_time && (
            <div className="pt-4 mt-4 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                <span className="font-medium">{t('labels.lastUpdated')}:</span>{' '}
                {formatTimestamp(existingConfig.last_updated_time)}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (mode === 'view') {
      return <div className="text-sm text-slate-600">{t('states.noAuthConfigured')}</div>;
    }

    // Edit mode - show form for selected auth type
    switch (selectedType) {
      case 'api_key':
        return (
          <APIKeyForm
            purpose={basePurpose}
            value={{
              name: formData.api_key?.name ?? '',
              value: formData.api_key?.value ?? '',
              location: formData.api_key?.location ?? 'header',
            }}
            onChange={apiKey => {
              setFormErrors(current => ({
                ...current,
                save: undefined,
              }));
              setFormData({ ...formData, api_key: apiKey });
            }}
            showApiKey={showApiKey}
            onToggleShowApiKey={() => setShowApiKey(!showApiKey)}
            nameError={visibleValidationErrors.apiKeyName}
            valueError={visibleValidationErrors.apiKeyValue}
          />
        );
      case 'basic_auth':
        return (
          <BasicAuthForm
            purpose={basePurpose}
            value={formData.basic_auth || { username: '', password: '' }}
            onChange={basicAuth => setFormData({ ...formData, basic_auth: basicAuth })}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            usernameError={visibleValidationErrors.basicAuthUsername}
            passwordError={visibleValidationErrors.basicAuthPassword}
          />
        );
      case 'bearer_token':
        return (
          <BearerTokenForm
            purpose={basePurpose}
            value={
              formData.bearer_token || {
                token: '',
                header_name: 'Authorization',
              }
            }
            onChange={bearerToken => setFormData({ ...formData, bearer_token: bearerToken })}
            showBearerToken={showBearerToken}
            onToggleShowBearerToken={() => setShowBearerToken(!showBearerToken)}
            tokenError={visibleValidationErrors.bearerTokenValue}
          />
        );
      case 'oauth2':
        return (
          <OAuth2Form
            purpose={basePurpose}
            value={
              formData.oauth2 || {
                client_id: '',
                client_secret: '',
                token_url: '',
                authorization_url: '',
                issuer: '',
                scopes: [],
                auth_params: {},
              }
            }
            onChange={oauth2 => setFormData({ ...formData, oauth2: oauth2 })}
            oauth2Info={oauth2Info}
            isLoadingOauth2Info={isLoadingOauth2Info}
            showClientSecret={showClientSecret}
            onToggleShowClientSecret={() => setShowClientSecret(!showClientSecret)}
            clientIdError={visibleValidationErrors.oauth2ClientId}
            clientSecretError={visibleValidationErrors.oauth2ClientSecret}
            tokenUrlError={visibleValidationErrors.oauth2TokenUrl}
            authorizationUrlError={visibleValidationErrors.oauth2AuthorizationUrl}
            issuerError={visibleValidationErrors.oauth2Issuer}
          />
        );
      case 'none':
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <NoneIcon />
            <p className="text-sm text-slate-600">{t('states.noConfigNeeded')}</p>
          </div>
        );
    }
  };

  // Phase 5: shared auth — template owner pre-configured; show read-only notice
  if (authMode === 'shared') {
    return (
      <div className="w-full">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900">{t('header.title')}</h2>
        </div>
        <div
          data-testid={domId('div', `manage-auth-${systemId}-shared-preconfigured`)}
          className="rounded-lg border border-blue-200 bg-blue-50 p-6 flex items-start gap-3"
        >
          <svg
            className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">{t('states.sharedPreconfigured')}</p>
            <p className="text-xs text-blue-700 mt-1">
              {t('states.sharedPreconfiguredDescription')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900">{t('header.title')}</h2>
        <p className="text-sm text-slate-600 mt-1">{t('header.subtitle')}</p>
      </div>

      {/* Body */}
      <form onSubmit={e => e.preventDefault()}>
        <div className="grid grid-cols-5 gap-6 min-h-[400px]">
          {/* Left side - Auth type list */}
          <div className="col-span-2 border border-[#B2BBC4] rounded-lg bg-white">
            <div className="p-4">
              <h3 className="text-xs font-semibold text-[#00152C] mb-3">{t('sections.type')}</h3>
              <div className="space-y-1">
                {AUTH_TYPES.map(type => {
                  const isSelected = selectedType === type.value;
                  // In edit mode, all auth types are enabled for switching
                  // In view mode, only the current type is enabled
                  const isDisabled =
                    !canManage ||
                    (type.value === 'oauth2' && !canUseOAuth) ||
                    (mode === 'view' && type.value !== existingConfig?.auth_type);
                  const isOriginalType = type.value === originalType;

                  return (
                    <Button
                      key={type.value}
                      data-testid={domId('btn', `${basePurpose}-type-${type.value}`)}
                      type="button"
                      onClick={() => handleSelectType(type.value)}
                      disabled={isDisabled}
                      variant="unstyled"
                      className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-colors border border-[#B2BBC4] ${
                        isSelected
                          ? 'bg-[#EBF5FF] border-[#4C6075] text-[#00152C]'
                          : 'border-transparent text-slate-700'
                      } ${
                        isDisabled
                          ? 'cursor-not-allowed opacity-60 bg-[#EBF5FF] border border-[#B2BBC4] '
                          : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {type.icon(isSelected)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="font-medium">{t(type.labelKey)}</div>
                            {/* Show badge for original/saved type in edit mode */}
                            {mode === 'edit' && isOriginalType && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                {t('states.current')}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {t(type.descriptionKey)}
                          </div>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right side - Configuration form */}
          <div className="col-span-3 border border-[#B2BBC4] rounded-lg bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-semibold text-[#00152C]">{t('sections.config')}</h3>
              {existingConfig && (
                <div className="flex items-center gap-2">
                  {canManage &&
                    (existingConfig.auth_type !== 'oauth2' || canUseOAuth) &&
                    mode === 'view' && (
                      <>
                        <Button
                          data-testid={domId('btn', `${basePurpose}-edit`)}
                          type="button"
                          onClick={startEditCurrent}
                          variant="unstyled"
                          className="text-sm text-blue-600 flex items-center gap-1"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          {t('actions.edit')}
                        </Button>
                        {/* <Button
                      type="button"
                      onClick={startChangeType}
                       variant="unstyled" className="text-sm text-slate-600 flex items-center gap-1"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                        />
                      </svg>
                      Change Type
                    </Button> */}
                      </>
                    )}
                </div>
              )}
            </div>

            <div className="flex-1">{renderContent()}</div>
            {formErrors.save && <p className="mt-4 text-sm text-red-600">{formErrors.save}</p>}
          </div>
        </div>

        {/* Footer with Save/Cancel buttons */}
        {canManage && (selectedType !== 'oauth2' || canUseOAuth) && mode === 'edit' && (
          <div className="flex items-center justify-end gap-3 mt-6 pt-4">
            <Button
              data-testid={domId('btn', `${basePurpose}-cancel`)}
              type="button"
              onClick={onCancel ? handleCancel : resetToSummary}
              disabled={isSaving}
              variant="unstyled"
              className="px-4 py-2 text-sm text-slate-600 disabled:opacity-50"
            >
              {t('actions.cancel')}
            </Button>
            <Button
              data-testid={domId('btn', `${basePurpose}-save`)}
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              data-loading={isSaving ? 'true' : undefined}
              variant="unstyled"
              className="px-4 py-2 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(90deg, #713D98, #3E3D98)' }}
            >
              {t('actions.saveFinish')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}

// Sub-components for form fields

// Reusable SensitiveInput component with show/hide toggle
interface SensitiveInputProps {
  purpose: string;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showValue: boolean;
  onToggleShow: () => void;
  error?: string;
}

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <span className="ml-1 text-red-500">*</span>
    </>
  );
}

function SensitiveInput({
  purpose,
  label,
  value,
  onChange,
  placeholder,
  showValue,
  onToggleShow,
  error,
}: SensitiveInputProps) {
  const t = useTranslations('ManageAuth');
  const inputId = domId('txt', purpose);
  const toggleId = domId('btn', `${purpose}-toggle-visibility`);
  return (
    <div>
      <label
        data-testid={domId('lbl', purpose)}
        className="block text-xs font-medium text-slate-600 mb-1"
        htmlFor={inputId}
      >
        {label}
      </label>
      <div className="relative">
        <Input
          id={inputId}
          data-testid={inputId}
          type={showValue ? 'text' : 'password'}
          aria-invalid={Boolean(error)}
          className={`w-full rounded-md border px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-1 ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <Button
          data-testid={toggleId}
          type="button"
          onClick={onToggleShow}
          variant="unstyled"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 transition-colors"
          title={showValue ? t('actions.hide') : t('actions.show')}
        >
          {showValue ? (
            // Eye slash icon (hide)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          ) : (
            // Eye icon (show)
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          )}
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function SensitiveValueDisplay({
  purpose,
  value,
  emptyValue,
}: {
  purpose: string;
  value?: string;
  emptyValue: string;
}) {
  const t = useTranslations('ManageAuth');
  const [isVisible, setIsVisible] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  const copyValue = () => {
    if (!value) return;
    void navigator.clipboard.writeText(value);
    setIsCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsCopied(false), 1000);
  };

  return (
    <span className="inline-flex items-center gap-1.5 align-middle">
      <span data-testid={domId('txt', purpose)} className="font-mono text-xs break-all">
        {!value ? emptyValue : isVisible ? value : '••••••••'}
      </span>
      {value && (
        <>
          <Button
            data-testid={domId('btn', `${purpose}-toggle-visibility`)}
            type="button"
            onClick={() => setIsVisible(visible => !visible)}
            variant="unstyled"
            className="p-1 text-slate-500"
            title={isVisible ? t('actions.hide') : t('actions.show')}
          >
            {isVisible ? 'Hide' : 'Show'}
          </Button>
          <Button
            data-testid={domId('btn', `${purpose}-copy`)}
            type="button"
            onClick={copyValue}
            variant="unstyled"
            className="p-1 text-slate-500"
            title={t('actions.copyToClipboard')}
          >
            {isCopied ? 'Copied' : 'Copy'}
          </Button>
        </>
      )}
    </span>
  );
}

interface APIKeyConfig {
  name: string;
  value: string;
  location: 'header' | 'query';
}

interface APIKeyFormProps {
  purpose: string;
  value: APIKeyConfig;
  onChange: (next: APIKeyConfig) => void;
  showApiKey: boolean;
  onToggleShowApiKey: () => void;
  nameError?: string;
  valueError?: string;
}

function APIKeyForm({
  purpose,
  value,
  onChange,
  showApiKey,
  onToggleShowApiKey,
  nameError,
  valueError,
}: APIKeyFormProps) {
  const t = useTranslations('ManageAuth');
  const purposeBase = `${purpose}-api-key`;
  return (
    <div className="space-y-4">
      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-header-name`)}
          htmlFor={domId('txt', `${purposeBase}-header-name`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          <RequiredLabel>{t('labels.headerName')}</RequiredLabel>
        </label>
        <Input
          id={domId('txt', `${purposeBase}-header-name`)}
          data-testid={domId('txt', `${purposeBase}-header-name`)}
          type="text"
          aria-invalid={Boolean(nameError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            nameError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.name}
          onChange={event => onChange({ ...value, name: event.target.value })}
          placeholder={t('placeholders.apiKeyName')}
        />
        {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
      </div>

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-location`)}
          htmlFor={domId('cbo', `${purposeBase}-location`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {t('labels.location')}
        </label>
        <Select
          value={value.location}
          onValueChange={selectedValue =>
            onChange({
              ...value,
              location: selectedValue as 'header' | 'query',
            })
          }
        >
          <SelectTrigger
            id={domId('cbo', `${purposeBase}-location`)}
            data-testid={domId('cbo', `${purposeBase}-location`)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem data-testid={domId('cbo', `${purposeBase}-location-header`)} value="header">
              {t('locations.header')}
            </SelectItem>
            <SelectItem data-testid={domId('cbo', `${purposeBase}-location-query`)} value="query">
              {t('locations.query')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <SensitiveInput
        purpose={`${purposeBase}-value`}
        label={<RequiredLabel>{t('labels.apiKey')}</RequiredLabel>}
        value={value.value}
        onChange={newValue => onChange({ ...value, value: newValue })}
        placeholder={t('placeholders.apiKeyValue')}
        showValue={showApiKey}
        onToggleShow={onToggleShowApiKey}
        error={valueError}
      />
    </div>
  );
}

interface BasicAuthConfig {
  username: string;
  password: string;
}

interface BasicAuthFormProps {
  purpose: string;
  value: BasicAuthConfig;
  onChange: (next: BasicAuthConfig) => void;
  showPassword: boolean;
  onToggleShowPassword: () => void;
  usernameError?: string;
  passwordError?: string;
}

function BasicAuthForm({
  purpose,
  value,
  onChange,
  showPassword,
  onToggleShowPassword,
  usernameError,
  passwordError,
}: BasicAuthFormProps) {
  const t = useTranslations('ManageAuth');
  const purposeBase = `${purpose}-basic-auth`;
  return (
    <div className="space-y-4">
      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-username`)}
          htmlFor={domId('txt', `${purposeBase}-username`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          <RequiredLabel>{t('labels.username')}</RequiredLabel>
        </label>
        <Input
          id={domId('txt', `${purposeBase}-username`)}
          data-testid={domId('txt', `${purposeBase}-username`)}
          type="text"
          aria-invalid={Boolean(usernameError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            usernameError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.username}
          onChange={event => onChange({ ...value, username: event.target.value })}
          placeholder={t('placeholders.username')}
        />
        {usernameError && <p className="mt-1 text-xs text-red-600">{usernameError}</p>}
      </div>

      <SensitiveInput
        purpose={`${purposeBase}-password`}
        label={<RequiredLabel>{t('labels.password')}</RequiredLabel>}
        value={value.password}
        onChange={newValue => onChange({ ...value, password: newValue })}
        placeholder={t('placeholders.password')}
        showValue={showPassword}
        onToggleShow={onToggleShowPassword}
        error={passwordError}
      />
    </div>
  );
}

interface BearerTokenConfig {
  token: string;
  header_name?: string;
}

interface BearerTokenFormProps {
  purpose: string;
  value: BearerTokenConfig;
  onChange: (next: BearerTokenConfig) => void;
  showBearerToken: boolean;
  onToggleShowBearerToken: () => void;
  tokenError?: string;
}

function BearerTokenForm({
  purpose,
  value,
  onChange,
  showBearerToken,
  onToggleShowBearerToken,
  tokenError,
}: BearerTokenFormProps) {
  const t = useTranslations('ManageAuth');
  const purposeBase = `${purpose}-bearer-token`;
  return (
    <div className="space-y-4">
      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-header-name`)}
          htmlFor={domId('txt', `${purposeBase}-header-name`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {t('labels.headerName')}
        </label>
        <Input
          id={domId('txt', `${purposeBase}-header-name`)}
          data-testid={domId('txt', `${purposeBase}-header-name`)}
          type="text"
          readOnly
          aria-readonly="true"
          className="w-full cursor-not-allowed rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-500 focus:outline-none"
          value={value.header_name ?? ''}
          placeholder={t('placeholders.bearerHeader')}
        />
      </div>

      <SensitiveInput
        purpose={`${purposeBase}-token`}
        label={<RequiredLabel>{t('labels.token')}</RequiredLabel>}
        value={value.token}
        onChange={newValue => onChange({ ...value, token: newValue })}
        placeholder={t('placeholders.token')}
        showValue={showBearerToken}
        onToggleShow={onToggleShowBearerToken}
        error={tokenError}
      />
    </div>
  );
}

interface OAuth2Config {
  client_id: string;
  // Optional: public clients (PKCE) may omit the secret. Aligned with
  // OAuth2ConfigSchema / AuthConfig.oauth2.
  client_secret?: string;
  token_url: string;
  authorization_url?: string;
  issuer?: string;
  scopes?: string[];
  auth_params?: Record<string, string>;
}

interface OAuth2FormProps {
  purpose: string;
  value: OAuth2Config;
  onChange: (next: OAuth2Config) => void;
  oauth2Info?: OauthInfo | null;
  isLoadingOauth2Info?: boolean;
  showClientSecret: boolean;
  onToggleShowClientSecret: () => void;
  clientIdError?: string;
  clientSecretError?: string;
  tokenUrlError?: string;
  authorizationUrlError?: string;
  issuerError?: string;
}

function OAuth2Form({
  purpose,
  value,
  onChange,
  oauth2Info,
  isLoadingOauth2Info,
  showClientSecret,
  onToggleShowClientSecret,
  clientIdError,
  clientSecretError,
  tokenUrlError,
  authorizationUrlError,
  issuerError,
}: OAuth2FormProps) {
  const t = useTranslations('ManageAuth');
  const purposeBase = `${purpose}-oauth2`;
  const [hasCopiedCallbackUrl, setHasCopiedCallbackUrl] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyCallback = useCallback(() => {
    if (!oauth2Info?.callback_url) return;
    navigator.clipboard.writeText(oauth2Info.callback_url);
    setHasCopiedCallbackUrl(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => {
      setHasCopiedCallbackUrl(false);
    }, 1200);
  }, [oauth2Info]);

  return (
    <div className="space-y-4">
      {/* Callback URL Info */}
      {isLoadingOauth2Info ? (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
          <div className="flex items-center gap-2 text-sm text-blue-700">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{t('states.loadingOauth2')}</span>
          </div>
        </div>
      ) : oauth2Info?.callback_url ? (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-blue-900 mb-1">
                {t('labels.oauthCallbackUrl')}
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white px-2 py-1.5 rounded border border-blue-200 text-blue-800 font-mono break-all">
                  {oauth2Info.callback_url}
                </code>
                <Button
                  type="button"
                  data-testid={domId('btn', `${purposeBase}-copy-callback-url`)}
                  onClick={handleCopyCallback}
                  variant="unstyled"
                  className={`flex-shrink-0 px-2 py-1.5 text-xs rounded border transition-all duration-200 ${
                    hasCopiedCallbackUrl
                      ? 'bg-blue-600 text-white border-blue-600 scale-105 shadow-sm'
                      : 'text-blue-700 border-transparent'
                  }`}
                  title={t('actions.copyToClipboard')}
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </Button>
              </div>
              <p className="text-xs text-blue-700 mt-1.5">{t('hints.oauthCallback')}</p>
            </div>
          </div>
        </div>
      ) : null}

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-client-id`)}
          htmlFor={domId('txt', `${purposeBase}-client-id`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          <RequiredLabel>{t('labels.clientId')}</RequiredLabel>
        </label>
        <Input
          id={domId('txt', `${purposeBase}-client-id`)}
          data-testid={domId('txt', `${purposeBase}-client-id`)}
          type="text"
          aria-invalid={Boolean(clientIdError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            clientIdError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.client_id}
          onChange={event => onChange({ ...value, client_id: event.target.value })}
          placeholder={t('placeholders.clientId')}
        />
        {clientIdError && <p className="mt-1 text-xs text-red-600">{clientIdError}</p>}
      </div>

      <SensitiveInput
        purpose={`${purposeBase}-client-secret`}
        label={<RequiredLabel>{t('labels.clientSecret')}</RequiredLabel>}
        value={value.client_secret ?? ''}
        onChange={newValue => onChange({ ...value, client_secret: newValue })}
        placeholder={t('placeholders.clientSecret')}
        showValue={showClientSecret}
        onToggleShow={onToggleShowClientSecret}
        error={clientSecretError}
      />

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-token-url`)}
          htmlFor={domId('txt', `${purposeBase}-token-url`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          <RequiredLabel>{t('labels.tokenUrl')}</RequiredLabel>
        </label>
        <Input
          id={domId('txt', `${purposeBase}-token-url`)}
          data-testid={domId('txt', `${purposeBase}-token-url`)}
          type="url"
          aria-invalid={Boolean(tokenUrlError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            tokenUrlError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.token_url}
          onChange={event => onChange({ ...value, token_url: event.target.value })}
          placeholder={t('placeholders.tokenUrl')}
        />
        {tokenUrlError && <p className="mt-1 text-xs text-red-600">{tokenUrlError}</p>}
      </div>

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-authorization-url`)}
          htmlFor={domId('txt', `${purposeBase}-authorization-url`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {t('labels.authorizationUrlOptional')}
        </label>
        <Input
          id={domId('txt', `${purposeBase}-authorization-url`)}
          data-testid={domId('txt', `${purposeBase}-authorization-url`)}
          type="url"
          aria-invalid={Boolean(authorizationUrlError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            authorizationUrlError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.authorization_url ?? ''}
          onChange={event => onChange({ ...value, authorization_url: event.target.value })}
          placeholder={t('placeholders.authorizationUrl')}
        />
        {authorizationUrlError && (
          <p className="mt-1 text-xs text-red-600">{authorizationUrlError}</p>
        )}
      </div>

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-issuer`)}
          htmlFor={domId('txt', `${purposeBase}-issuer`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {requestsOpenID(value.scopes) ? <RequiredLabel>{t('labels.issuer')}</RequiredLabel> : t('labels.issuerOptional')}
        </label>
        <Input
          id={domId('txt', `${purposeBase}-issuer`)}
          data-testid={domId('txt', `${purposeBase}-issuer`)}
          type="url"
          aria-invalid={Boolean(issuerError)}
          className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
            issuerError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500'
          }`}
          value={value.issuer ?? ''}
          onChange={event => onChange({ ...value, issuer: event.target.value })}
          placeholder={t('placeholders.issuer')}
        />
        <p className="mt-1 text-xs text-slate-500">{t('hints.issuer')}</p>
        {issuerError && <p className="mt-1 text-xs text-red-600">{issuerError}</p>}
      </div>

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-scopes`)}
          htmlFor={domId('txt', `${purposeBase}-scopes-input`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {t('labels.scopes')}
        </label>
        <div className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 min-h-[38px] flex flex-wrap gap-1.5 items-center">
          {value.scopes?.map((scope, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200"
            >
              {scope}
              <Button
                type="button"
                data-testid={domId('btn', `${purposeBase}-remove-scope-${scope}`)}
                onClick={() => {
                  const newScopes = value.scopes?.filter((_, i) => i !== index) ?? [];
                  onChange({ ...value, scopes: newScopes });
                }}
                variant="unstyled"
                className="rounded-sm p-0.5 transition-colors"
              >
                <svg
                  className="h-3 w-3"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Button>
            </span>
          ))}
          <Input
            id={domId('txt', `${purposeBase}-scopes-input`)}
            data-testid={domId('txt', `${purposeBase}-scopes-input`)}
            type="text"
            className="flex-1 min-w-[120px] outline-none text-sm"
            placeholder={value.scopes?.length ? '' : t('placeholders.scopes')}
            onKeyDown={event => {
              if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                const inputValue = event.currentTarget.value.trim();
                if (inputValue) {
                  const newScopes = [...(value.scopes ?? []), inputValue];
                  onChange({ ...value, scopes: newScopes });
                  event.currentTarget.value = '';
                }
              } else if (
                event.key === 'Backspace' &&
                event.currentTarget.value === '' &&
                value.scopes?.length
              ) {
                const newScopes = value.scopes.slice(0, -1);
                onChange({ ...value, scopes: newScopes });
              }
            }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">{t('hints.scopes')}</p>
      </div>

      <div>
        <label
          data-testid={domId('lbl', `${purposeBase}-auth-params`)}
          className="block text-xs font-medium text-slate-600 mb-1"
        >
          {t('labels.authParamsOptional')}
        </label>
        <div className="space-y-2">
          {Object.entries(value.auth_params || {}).map(([key, val], index) => (
            <div key={index} className="flex gap-2">
              <Input
                data-testid={domId(
                  'txt',
                  `${purposeBase}-auth-param-key-${key || `unnamed-${index}-ref`}`
                )}
                type="text"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={key}
                onChange={event => {
                  const newParams = { ...(value.auth_params || {}) };
                  delete newParams[key];
                  newParams[event.target.value] = val;
                  onChange({ ...value, auth_params: newParams });
                }}
                placeholder={t('placeholders.authParamKey')}
              />
              <Input
                data-testid={domId(
                  'txt',
                  `${purposeBase}-auth-param-value-${key || `unnamed-${index}-ref`}`
                )}
                type="text"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={val}
                onChange={event => {
                  const newParams = { ...(value.auth_params || {}) };
                  newParams[key] = event.target.value;
                  onChange({ ...value, auth_params: newParams });
                }}
                placeholder={t('placeholders.authParamValue')}
              />
              <Button
                type="button"
                data-testid={domId(
                  'btn',
                  `${purposeBase}-delete-auth-param-${key || `unnamed-${index}-ref`}`
                )}
                onClick={() => {
                  const newParams = { ...(value.auth_params || {}) };
                  delete newParams[key];
                  onChange({ ...value, auth_params: newParams });
                }}
                variant="unstyled"
                className="px-3 py-2 text-sm text-red-600 rounded-md transition-colors"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </Button>
            </div>
          ))}
          <Button
            type="button"
            data-testid={domId('btn', `${purposeBase}-add-auth-param`)}
            onClick={() => {
              const newParams = { ...(value.auth_params || {}), '': '' };
              onChange({ ...value, auth_params: newParams });
            }}
            variant="unstyled"
            className="cursor-pointer w-full px-3 py-2 text-sm text-blue-600 rounded-md border border-dashed border-blue-300 transition-colors"
          >
            {t('actions.addParameter')}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">{t('hints.authParams')}</p>
      </div>
    </div>
  );
}
